import React, { useState } from 'react';
import { AlertCircle, Send, X } from 'lucide-react';

interface IncidentManagerProps {
    onAdd: (type: 'bovin' | 'eboulement' | 'promeneur' | 'autre', pk: number, description: string) => void;
    currentPK: number | null;
    role: 'driver' | 'admin' | null;
    isMobile?: boolean;
    isEmbedded?: boolean;
}

export default function IncidentManager({ onAdd, currentPK, role, isMobile, isEmbedded = false }: IncidentManagerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [type, setType] = useState<'bovin' | 'eboulement' | 'promeneur' | 'autre'>('bovin');
    const [pk, setPk] = useState<string>('0.000');
    const [description, setDescription] = useState('');

    const handleOpen = () => {
        if (role === 'driver') {
            // Le conducteur fige le PK au moment précis du clic
            setPk((currentPK || 0).toFixed(3));
        }
        setIsOpen(true);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onAdd(type, parseFloat(pk), description || type);
        setIsOpen(false);
        setDescription('');
    };

    return (
        <div className={`${isEmbedded ? 'flex flex-col items-center justify-center' : `fixed z-[2000] pointer-events-auto ${role === 'driver' ? 'bottom-[6rem] right-4' : (isMobile ? 'bottom-24 right-4' : 'bottom-16 right-10')} flex flex-col items-end gap-4`}`}>
            {isOpen && (
                <form
                    onSubmit={handleSubmit}
                    className={`glass-panel-heavy p-6 rounded-[2.5rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] border-white/20 ${isMobile ? 'w-[calc(100vw-2rem)] max-w-sm' : 'w-80'} space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300 ${isEmbedded ? 'absolute bottom-0 right-full mr-6 z-[5000]' : ''}`}
                >
                    <div className="flex justify-between items-center">
                        <h3 className="text-white font-black uppercase text-xs tracking-[0.2em] flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                            Signalement
                        </h3>
                        <button type="button" onClick={() => setIsOpen(false)} className="p-2 text-slate-500 hover:text-white transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-[9px] text-cyan-500 font-black tracking-[0.2em] block uppercase mb-2 ml-1">Nature de l'incident</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value as any)}
                                className={`w-full bg-white/5 border border-white/10 ${isMobile ? 'p-4 text-sm' : 'p-3 text-xs'} rounded-2xl text-white outline-none focus:border-cyan-500 transition-all appearance-none cursor-pointer`}
                            >
                                <option value="bovin">🐄 Bovins / Animaux</option>
                                <option value="eboulement">⚠️ Éboulement / Obstacle</option>
                                <option value="promeneur">🚶 Promeneurs / Public</option>
                                <option value="autre">❓ Autre</option>
                            </select>
                        </div>

                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="text-[9px] text-cyan-500 font-black tracking-[0.2em] block uppercase mb-2 ml-1">
                                    {role === 'driver' ? 'PK (Auto-Capturé)' : 'Point Kilométrique'}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={pk}
                                        onChange={(e) => setPk(e.target.value)}
                                        disabled={role === 'driver'}
                                        className={`w-full bg-white/5 border border-white/10 ${isMobile ? 'p-4 text-sm' : 'p-3 text-xs'} rounded-2xl text-white outline-none focus:border-cyan-500 font-mono font-bold ${role === 'driver' ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        placeholder="0.000"
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-cyan-500 rounded-full shadow-[0_0_8px_cyan]"></div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-[9px] text-cyan-500 font-black tracking-[0.2em] block uppercase mb-2 ml-1">Détails (Optionnel)</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl text-white outline-none focus:border-cyan-500 transition-all text-sm h-24 resize-none placeholder:text-slate-600"
                                placeholder="Précisez ici..."
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className={`w-full bg-red-600 hover:bg-red-500 text-white ${isMobile ? 'py-5 text-sm' : 'py-4 text-[10px]'} rounded-2xl font-black uppercase tracking-[0.3em] transition-all shadow-[0_0_20px_rgba(220,38,38,0.3)] active:scale-95 flex items-center justify-center gap-3`}
                    >
                        <Send className={isMobile ? "w-5 h-5" : "w-4 h-4"} />
                        Transmettre
                    </button>
                </form>
            )}

            {!isOpen && (
                <button
                    onClick={handleOpen}
                    className={`bg-red-600 hover:bg-red-500 text-white w-[50px] h-[50px] ${isMobile ? 'shadow-[0_0_60px_rgba(220,38,38,0.6)]' : 'shadow-[0_0_30px_rgba(220,38,38,0.5)]'} rounded-2xl flex items-center justify-center transition-all transform hover:scale-105 active:scale-90 group border-4 border-white/20`}
                >
                    <AlertCircle className="w-7 h-7" />
                    {!isMobile && (
                        <span className="absolute right-full mr-6 top-1/2 -translate-y-1/2 glass-panel px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap shadow-xl">
                            Signaler
                        </span>
                    )}
                </button>
            )}
        </div>
    );
}
